import React, { useState } from "react";
import { Copy, Plus, Trash2, Check, X, Pencil } from "lucide-react";

export const SCENARIO_PALETTE = ["#0B3B60", "#c0873b", "#3b7d5a"];

/**
 * Scenario switcher shown on the Assumptions/Cash-Flow and Projection steps.
 * Lets the user switch, rename, duplicate (clone the current scenario) and
 * delete scenarios. Shared steps (About You / Investments) do not show this.
 */
export default function ScenarioTabs({ scenarios, activeId, onSelect, onRename, onDuplicate, onDelete, max = 3 }) {
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState("");

    const startEdit = (sc) => { setEditingId(sc.id); setDraft(sc.name || ""); };
    const commit = () => {
        if (editingId) onRename(editingId, (draft || "").trim() || "Scenario");
        setEditingId(null); setDraft("");
    };

    return (
        <div className="mb-6 rounded-2xl border border-oth-line/70 bg-white px-3 py-3" data-testid="scenario-tabs">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body pl-1 pr-1">Scenarios</span>
                {scenarios.map((sc, i) => {
                    const active = sc.id === activeId;
                    const color = SCENARIO_PALETTE[i % SCENARIO_PALETTE.length];
                    return (
                        <div key={sc.id} className={`flex items-center rounded-full border transition-colors ${active ? "border-transparent text-white" : "border-oth-line bg-white text-oth-ink2 hover:border-oth-blue"}`} style={active ? { backgroundColor: "#0A0A0A" } : {}} data-testid={`scenario-tab-${i}`}>
                            {editingId === sc.id ? (
                                <div className="flex items-center gap-1 pl-3 pr-1 py-1">
                                    <input
                                        autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setEditingId(null); setDraft(""); } }}
                                        maxLength={40}
                                        className="bg-transparent text-[13px] font-body outline-none w-32 border-b border-oth-line"
                                        data-testid={`scenario-rename-input-${i}`}
                                    />
                                    <button onClick={commit} className="w-6 h-6 rounded-full hover:bg-black/10 flex items-center justify-center" data-testid={`scenario-rename-save-${i}`}><Check size={13} /></button>
                                    <button onClick={() => { setEditingId(null); setDraft(""); }} className="w-6 h-6 rounded-full hover:bg-black/10 flex items-center justify-center"><X size={13} /></button>
                                </div>
                            ) : (
                                <>
                                    <button onClick={() => onSelect(sc.id)} className="flex items-center gap-2 pl-3.5 pr-2 py-2 text-[13px] font-body" data-testid={`scenario-select-${i}`}>
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                                        {sc.name || `Scenario ${i + 1}`}
                                    </button>
                                    {active && (
                                        <div className="flex items-center pr-1.5">
                                            <button onClick={() => startEdit(sc)} className="w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center" title="Rename" data-testid={`scenario-edit-${i}`}><Pencil size={12} /></button>
                                            {scenarios.length > 1 && (
                                                <button onClick={() => onDelete(sc.id)} className="w-6 h-6 rounded-full hover:bg-white/20 flex items-center justify-center" title="Delete" data-testid={`scenario-delete-${i}`}><Trash2 size={12} /></button>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
                {scenarios.length < max && (
                    <button onClick={onDuplicate} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-dashed border-oth-line text-[13px] font-body text-oth-ink2 hover:border-oth-blue hover:text-oth-blue transition-colors" data-testid="scenario-duplicate-btn">
                        <Copy size={13} /> Duplicate scenario
                    </button>
                )}
            </div>
            <p className="text-[11px] text-oth-ink3 font-body mt-2 pl-1 leading-relaxed">
                About You & Your Investments are shared across all scenarios. Assumptions and cash-flow &amp; goals are unique to each scenario. {scenarios.length >= max && <span className="text-[#9c6a1c]">Maximum of {max} scenarios.</span>}
            </p>
        </div>
    );
}
